import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { QtyStepper, PageHeader } from "@/components/ui-bits";
import { eur, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const Winkelwagen = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, pick } = useLang();
  const { items, setQty, remove, subtotal, coupon, applyCoupon } = useCart();
  const [code, setCode] = useState("");
  const [couponMsg, setCouponMsg] = useState("");
  const [couponError, setCouponError] = useState("");

  const discount = coupon?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  const apply = async (e) => {
    e.preventDefault();
    setCouponMsg("");
    setCouponError("");
    try {
      const d = await applyCoupon(code);
      if (d.valid) setCouponMsg(t("cart.couponOk").replace("{code}", d.code));
      else if (d.reason === "min_total") setCouponError(t("cart.couponMin").replace("{min}", eur(d.min_total)));
      else setCouponError(t("cart.couponBad"));
    } catch (err) {
      setCouponError(formatApiError(err));
    }
  };

  const goCheckout = () => {
    if (!user) {
      navigate("/inloggen", { state: { next: "/checkout" } });
    } else {
      navigate("/checkout");
    }
  };

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-32 pb-24">
      <PageHeader eyebrow={t("cart.eyebrow")} title={<>{t("cart.title1")} <span className="italic normal-case text-gold">{t("cart.title2")}</span></>} />

      {items.length === 0 ? (
        <div data-testid="cart-empty-state" className="border border-gold/15 bg-ink px-8 py-20 text-center">
          <ShoppingBag size={28} className="text-gold/60 mx-auto mb-6" />
          <p className="font-serif text-2xl text-cream">{t("cart.empty")}</p>
          <p className="mt-3 text-sm text-mist">{t("cart.emptySub")}</p>
          <button data-testid="cart-empty-shop-button" onClick={() => navigate("/winkel")} className="mt-8 bg-gold text-obsidian font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-gold-light transition-colors">
            {t("cart.emptyCta")}
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[2fr_1fr] gap-12">
          <div className="space-y-6">
            {items.map((item) => {
              const unit = item.discount_price || item.price;
              const isStudio = item.image_style === "studio";
              return (
                <div key={item.slug} data-testid={`cart-item-${item.slug}`} className="flex gap-5 border border-gold/10 bg-ink p-4">
                  <div className={`w-24 h-28 shrink-0 overflow-hidden ${isStudio ? "bg-[#ECEAE3]" : "bg-elevate"}`}>
                    <img src={item.image_url} alt={pick(item, "model_fr") || item.model} className={`w-full h-full ${isStudio ? "object-contain p-2" : "object-cover"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gold/80">{item.brand}</p>
                        <p className="font-serif text-lg text-cream leading-snug mt-1">{pick(item, "model")}</p>
                        <p className="font-mono text-[10px] text-smoke mt-1">{item.ml}</p>
                      </div>
                      <button data-testid={`cart-remove-${item.slug}`} onClick={() => remove(item.slug)} aria-label={t("cart.remove")} className="text-smoke hover:text-red-400 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="mt-4 flex items-center justify-between">
                      <QtyStepper value={item.quantity} onChange={(v) => setQty(item.slug, v)} max={item.stock || 99} testid={`cart-qty-${item.slug}`} />
                      <p className="font-serif text-lg text-gold">{eur(unit * item.quantity)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="h-fit lg:sticky lg:top-28 border border-gold/20 bg-elevate p-8">
            <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">{t("cart.summary")}</h2>

            <form data-testid="coupon-form" onSubmit={apply} className="flex gap-2 mb-2">
              <input
                data-testid="coupon-input"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t("cart.couponPh")}
                className="flex-1 bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream placeholder:text-smoke focus:outline-none focus:border-gold/50"
              />
              <button type="submit" data-testid="coupon-apply-button" className="border border-gold/40 text-gold font-mono text-[10px] tracking-[0.2em] uppercase px-5 hover:bg-gold/10 transition-colors">
                {t("cart.apply")}
              </button>
            </form>
            {couponMsg && <p data-testid="coupon-success" className="text-xs text-emerald-300 mb-4">{couponMsg}</p>}
            {couponError && <p data-testid="coupon-error" className="text-xs text-red-400 mb-4">{couponError}</p>}

            <div className="space-y-3 border-t border-gold/10 pt-5 mt-5 text-sm">
              <div className="flex justify-between text-mist">
                <span>{t("cart.subtotal")}</span>
                <span data-testid="cart-subtotal">{eur(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-gold">
                  <span>{t("cart.discount")} ({coupon.code})</span>
                  <span data-testid="cart-discount">−{eur(discount)}</span>
                </div>
              )}
              <p className="font-mono text-[10px] text-smoke tracking-[0.15em] uppercase pt-2">
                {t("cart.shippingNote")}
              </p>
              <div className="flex justify-between items-baseline border-t border-gold/10 pt-4">
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">{t("cart.total")}</span>
                <span data-testid="cart-total" className="font-serif text-2xl text-gold">{eur(total)}</span>
              </div>
            </div>

            <button
              data-testid="checkout-button"
              onClick={goCheckout}
              className="w-full mt-7 bg-gold text-obsidian font-mono text-xs tracking-[0.3em] uppercase py-4 hover:bg-gold-light transition-colors"
            >
              {t("cart.checkout")}
            </button>
            <button data-testid="continue-shopping-button" onClick={() => navigate("/winkel")} className="w-full mt-3 border border-cream/15 text-mist font-mono text-[10px] tracking-[0.25em] uppercase py-3.5 hover:text-cream hover:border-cream/40 transition-colors">
              {t("cart.continue")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Winkelwagen;
